import { createContext, useContext, useState, useEffect, ReactNode, useRef } from 'react';
import { auth, authStorage } from '../api/authApi';

export interface User {
  id?: string;
  name: string;
  email: string;
  avatar?: string | null;
}

interface UserContextType {
  currentUser: User | null;
  setCurrentUser: (user: User | null) => void;
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitialized: boolean; // New flag to track initialization completion
  login: (user: User, token: string) => void;
  logout: () => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider = ({ children }: UserProviderProps) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const loginInProgressRef = useRef(false);

  // Check for existing auth on mount
  useEffect(() => {
    const checkAuth = async () => {
      if (loginInProgressRef.current) {
        return; // Don't interfere with ongoing login
      }

      const token = authStorage.getToken();
      const savedUser = authStorage.getUser();

      if (token && savedUser) {
        try {
          // Set saved user first for faster initial state
          setCurrentUser(savedUser);
          
          // Then validate with server (but don't block UI)
          const user = await auth.getCurrentUser();
          if (JSON.stringify(user) !== JSON.stringify(savedUser)) {
            setCurrentUser(user);
          }
        } catch (error) {
          console.log('Token validation failed, clearing auth data:', error);
          // Token validation failed, clear storage
          authStorage.clearAuth();
          setCurrentUser(null);
        }
      }
      setIsLoading(false);
      setIsInitialized(true);
    };

    checkAuth();
  }, []);

  const login = (user: User, token: string) => {
    loginInProgressRef.current = true;
    
    try {
      // Atomic storage update using centralized storage
      authStorage.setAuth(token, user);
      
      // Update state after storage is complete
      setCurrentUser(user);
      setIsInitialized(true);
    } finally {
      loginInProgressRef.current = false;
    }
  };

  const logout = async () => {
    loginInProgressRef.current = true;
    
    try {
      // Clear state first for immediate UI update
      setCurrentUser(null);
      
      // Clear storage using centralized method
      authStorage.clearAuth();
      
      // Then attempt API logout (don't block on this)
      try {
        await auth.logout();
      } catch (error) {
        console.warn('Logout API call failed:', error);
        // Continue with logout even if API call fails
      }
    } finally {
      loginInProgressRef.current = false;
    }
  };

  const isAuthenticated = !!currentUser;

  return (
    <UserContext.Provider value={{ 
      currentUser, 
      setCurrentUser, 
      isAuthenticated, 
      isLoading,
      isInitialized,
      login,
      logout
    }}>
      {children}
    </UserContext.Provider>
  );
};